/**
 * Login-provider definitions for partner OIDC integrations (CBC/Radio-Canada,
 * ZDF, RTBF, KRO-NCRV, SRG, plus Auth0/stub for testing).
 *
 * Each `LoginProvider` is a static config bundle: client ID, authorize URL,
 * scopes, the redirect URI baked into the provider's allowlist, OAuth response
 * mode (`code` or `fragment`), and the silos allowed to use it. `silos` is the
 * access-control list — the login screen filters providers by the current silo.
 * `silosWithoutGoogleLogin` lists silos where Google login is suppressed
 * because the partner has its own auth and adding a second path would split
 * user accounts. Adding a new partner means a new `LoginProvider` constant plus
 * registration via `registerLoginProviders`.
 */

import { SRG_BU_LOGIN_BY_HOSTNAME } from './srg-domains';
import { getApiBaseUrl } from './util';

export type LoginProvider = {
    clientId: string;
    authUrl: string;
    extraParams: Record<string, string>;
    scope?: string;
    mode: 'code' | 'fragment';
    name: string;
    key: string;
    icon?: string;
    silos: string[];
    noPopup?: boolean;
};

export const rcIntLogin: LoginProvider = {
    key: 'rcInt',
    clientId: '77d1b085-1c39-4f0e-b6ce-92acaf992295',
    authUrl:
        'https://int-login.cbc.radio-canada.ca/0aa1e8c2-5242-47a4-996a-6ed694ee7127/B2C_1A_ExternalClient_FrontEnd_Login/oauth2/v2.0/authorize',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://conversations.radio-canada.ca/rc/login/one/fragmentRedirect',
        scope: 'openid https://rcmnb2cint.onmicrosoft.com/1d0b826c-646a-472a-9d3a-fdf4441895e9/email https://rcmnb2cint.onmicrosoft.com/1d0b826c-646a-472a-9d3a-fdf4441895e9/profile',
        ui_locales: 'fr',
    },
    mode: 'fragment',
    name: 'Radio-Canada Internal',
    silos: ['global', 'demo', 'test', 'rcInt'],
    icon: 'rc.svg',
};

export const rcLogin: LoginProvider = {
    key: 'rc',
    clientId: '32c46880-ae6e-429c-a437-97bab2c26f86',
    authUrl:
        'https://login.cbc.radio-canada.ca/bef1b538-1950-4283-9b27-b096cbc18070/B2C_1A_ExternalClient_FrontEnd_Login/oauth2/v2.0/authorize',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://conversations.radio-canada.ca/rc/login/one/fragmentRedirect',
        scope: 'openid https://rcmnb2cprod.onmicrosoft.com/84593b65-0ef6-4a72-891c-d351ddd50aab/email https://rcmnb2cprod.onmicrosoft.com/84593b65-0ef6-4a72-891c-d351ddd50aab/profile',
        ui_locales: 'fr',
    },
    mode: 'fragment',
    name: 'Radio-Canada',
    icon: 'rc.svg',
    silos: ['global', 'demo', 'test', 'rc'],
};

export const cbcIntLogin: LoginProvider = {
    key: 'cbcInt',
    clientId: '9ff39f62-d5ba-4899-b24e-414f22c12d01',
    authUrl:
        'https://int-login.cbc.radio-canada.ca/0aa1e8c2-5242-47a4-996a-6ed694ee7127/B2C_1A_ExternalClient_FrontEnd_Login_CBC/oauth2/v2.0/authorize',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://psi.newpublic.org/cbc/login/one/fragmentRedirect',
        scope: 'openid https://rcmnb2cint.onmicrosoft.com/1d0b826c-646a-472a-9d3a-fdf4441895e9/email',
        ui_locales: 'en',
    },
    mode: 'fragment',
    name: 'CBC Integration',
    silos: ['global', 'demo', 'test', 'cbcInt'],
    icon: 'rc.svg',
};

export const cbcLogin: LoginProvider = {
    key: 'cbc',
    clientId: '696644d6-036c-41d7-9983-ef45763d1648',
    authUrl:
        'https://login.cbc.radio-canada.ca/bef1b538-1950-4283-9b27-b096cbc18070/B2C_1A_ExternalClient_FrontEnd_Login_CBC/oauth2/v2.0/authorize',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://psi.newpublic.org/cbc/login/one/fragmentRedirect',
        scope: 'openid https://rcmnb2cprod.onmicrosoft.com/84593b65-0ef6-4a72-891c-d351ddd50aab/email',
        ui_locales: 'en',
    },
    mode: 'fragment',
    name: 'CBC',
    icon: 'rc.svg',
    silos: ['global', 'demo', 'test', 'cbc', 'cbcInt'],
};

export const zdfProdLogin: LoginProvider = {
    key: 'zdfProd',
    clientId: 'PSI-Prodsystem',
    authUrl: 'https://psi-login.zdf.de/realms/PSI/protocol/openid-connect/auth',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://psi.zdf.de/zdf/login/one/fragmentRedirect',
        scope: 'openid email',
        grant_type: 'client_credentials',
        kc_locale: 'de',
    },
    mode: 'fragment',
    name: 'ZDFspaces',
    silos: ['global', 'demo', 'test', 'zdf'],
};

export const zdfIntLogin: LoginProvider = {
    key: 'zdfInt',
    clientId: 'psi-staging-01',
    authUrl: 'https://login.zdf.digital/realms/PSI/protocol/openid-connect/auth',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://main.psi.zdf.digital/zdf/login/one/fragmentRedirect',
        scope: 'openid email',
        grant_type: 'client_credentials',
        kc_locale: 'de',
    },
    mode: 'fragment',
    name: 'ZDF Internal',
    icon: 'ZDF_logo.svg',
    silos: ['global', 'demo', 'test', 'zdf'],
};

export const kikaLogin: LoginProvider = {
    key: 'kika',
    clientId: 'psi-kika-logo',
    authUrl: 'https://login.zdf.digital/realms/psi-kika/protocol/openid-connect/auth',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://psi.kika.de/kika/login/one/fragmentRedirect',
        scope: 'openid email roles',
        grant_type: 'client_credentials',
        kc_locale: 'de',
    },
    mode: 'fragment',
    name: 'Login',
    silos: ['kika'],
};

export const rtbfLogin: LoginProvider = {
    key: 'rtbf',
    // noPopup: true,
    clientId: 'eJWPprwEyblkKj-nP28K-pmi',
    authUrl: 'https://login.rtbf.be/oidc/op/v1.0/3_kWKuPgcdAybqnqxq_MvHVk0-6PN8Zk8pIIkJM_yXOu-qLPDDsGOtIDFfpGivtbeO/authorize',
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        redirect_uri: 'https://psi.newpublic.org/global/login/one/fragmentRedirect',
        // scope: 'openid email_psi profile_psi',
        scope: 'openid email profile',
    },
    mode: 'fragment',
    name: 'RTBF',
    icon: 'rtbf.svg',
    silos: ['global', 'demo', 'test', 'rtbf'],
};

export const KroNcrvNpoIdTestLogin: LoginProvider = {
    key: 'KroNcrvNpoIdTest',
    clientId: 'kro-ncrv-psi-web-test',
    authUrl: 'https://npoid-sandbox.npoid-dev.nl/connect/authorize',
    extraParams: {
        response_type: 'code',
        scope: 'openid email profile',
    },
    mode: 'code',
    name: 'KRO-NCRV',
    icon: 'kro-ncrv_npo-id.svg',
    silos: ['global', 'demo', 'test', 'kro-ncrv'],
};
export function resolveSrgLogin(hostname?: string): LoginProvider {
    const h = hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
    const bu = SRG_BU_LOGIN_BY_HOSTNAME[h];
    if (bu) {
        return {
            key: bu.key,
            clientId: bu.clientId,
            authUrl: 'https://account.srgssr.ch/authz-srv/authz',
            extraParams: {
                response_type: 'code',
                scope: 'openid email profile',
                ui_locales: bu.uiLocale,
            },
            mode: 'code',
            name: bu.name,
            silos: ['global', 'one', 'demo', 'test', 'srg'],
        };
    }
    // Fallback: integration environment used by psi2.dev / psi1.dev.
    return {
        key: 'srgInt',
        clientId: 'ade2b5ce-bdeb-4116-a79f-bacfe9468256',
        authUrl: 'https://account-int.srgssr.ch/authz-srv/authz',
        extraParams: {
            response_type: 'code',
            scope: 'openid email profile',
            ui_locales: 'de',
        },
        mode: 'code',
        name: 'SRG',
        silos: ['global', 'one', 'demo', 'test', 'srg'],
    };
}

export const srgIntLogin: LoginProvider = resolveSrgLogin();

export const eoLogin: LoginProvider = {
    key: 'eo',
    clientId: process.env.EXPO_PUBLIC_EO_CLIENT_ID ?? 'hqrXvFtsA6KunReOwsk8uLIBMvMvdIcP',
    authUrl: process.env.EXPO_PUBLIC_EO_AUTH_URL ?? 'https://login.staging.eo.nl/authorize',
    extraParams: {
        response_type: 'code',
        scope: 'openid email profile',
    },
    mode: 'code',
    name: 'Inloggen met EO',
    silos: ['eo'],
};

export const auth0Login: LoginProvider = {
    key: 'auth0',
    clientId: 'Temkkdm1H8HMRPKBGh4fASzGrf0BgUid',
    authUrl: 'https://dev-gjts25ows86weesy.us.auth0.com/authorize',
    extraParams: {
        response_type: 'code',
        scope: 'openid email profile',
    },
    mode: 'code',
    name: 'Auth0 (Test)',
    silos: ['test'],
};

export const stubOidcLogin: LoginProvider = {
    key: 'stubOidc',
    clientId: 'stub-oidc-client-id',
    authUrl: `${getApiBaseUrl()}stub-oidc/authorize`,
    extraParams: {
        response_type: 'code',
        scope: 'openid email profile',
    },
    mode: 'code',
    name: 'Stub OIDC (Test)',
    silos: ['global', 'demo', 'test', 'e2etest'],
};

export const stubOidcFragmentLogin: LoginProvider = {
    key: 'stubOidcFragment',
    clientId: 'stub-oidc-client-id',
    authUrl: `${getApiBaseUrl()}stub-oidc/authorize`,
    extraParams: {
        response_type: 'id_token',
        response_mode: 'fragment',
        scope: 'openid email profile',
    },
    mode: 'fragment',
    name: 'Stub OIDC Fragment (Test)',
    silos: ['global', 'demo', 'test', 'e2etest'],
};

export const silosWithoutGoogleLogin = ['rc', 'rcInt', 'rtbf', 'cbc', 'cbcInt', 'srg', 'kika', 'eo'];
